// src/pages/Home.tsx
import * as React from "react";
import { Box, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { IMAGE_BASE_PATH } from "../../config/paths";
import ActionSection from "./ActionSection";
import BannerSection from "./BannerSection";
import { useSeo } from "../../components/seo/useSeo";
import { SITE_URL } from "../../config/site";
import { useJsonLd } from "../../components/seo/useJsonLd";

const visuallyHiddenSx = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

const Home: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const titleId = "home-title";
  const heroDescId = "home-hero-desc";

  // =========================
  // SEO hooks (sem Helmet)
  // =========================
  const seoTitle =
    t("HomePage.seoTitle") || "Core Latam — Kitesurf, Wing e Sustentabilidade";

  const seoDescription =
    t("HomePage.seoDescription") ||
    "Equipamentos e experiências para esportes aquáticos. Conheça nossa loja, revenda e iniciativas de sustentabilidade.";

  // useSeo -> aplica title/description/OG/Twitter/robots + canonical
  useSeo({
    title: seoTitle,
    description: seoDescription,
    canonical: `${SITE_URL}/`,
    image: `${SITE_URL}${IMAGE_BASE_PATH}home/VideoPlaceHolder.avif`,
    type: "website",
    noindex: false,
  });

  // JSON-LD (Organization)
  useJsonLd("org", {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Core Latam",
    url: `${SITE_URL}/`,
    // Se você tiver logo público, descomente:
    logo: `${SITE_URL}${IMAGE_BASE_PATH}logo.png`,
    sameAs: [
      "https://www.instagram.com/corekites.latam",
    ],
  });

  // JSON-LD (WebSite)
  useJsonLd("website", {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Core Latam",
    url: `${SITE_URL}/`,
    inLanguage: "pt-BR",
  });

  return (
    <Box component="main" aria-labelledby={titleId}>
      {/* H1 obrigatório p/ SEO (mesmo invisível visualmente) */}
      <Box component="h1" id={titleId} sx={visuallyHiddenSx}>
        {seoTitle}
      </Box>

      {/* Banner / Hero */}
      <Box component="section" aria-describedby={heroDescId} sx={{ width: "100%" }}>
        <Grid container>
          <Grid size={12} sx={{ height: { xs: "100vh", sm: "80vh" } }}>
            <BannerSection t={t} heroDescId={heroDescId} />
          </Grid>
        </Grid>
      </Box>

      {/* Ações principais */}
      <Box
        component="section"
        aria-label={t("HomePage.acoesPrincipais") || "Ações principais"}
      >
        <Grid container sx={{ width: "100%", m: 0 }}>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <ActionSection
              imageUrl={`${IMAGE_BASE_PATH}home/LojaBannerDegrade.avif`}
              title={t("HomePage.loja")}
              href="https://core-esportes-aquaticos.lojaintegrada.com.br/"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <ActionSection
              imageUrl={`${IMAGE_BASE_PATH}home/RevendedorBannerDegrade.avif`}
              title={t("HomePage.revendedor")}
              href="/pontovenda"
              onClick={() => navigate("/pontovenda")}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <ActionSection
              imageUrl={`${IMAGE_BASE_PATH}home/SustentabilidadeBannerDegrade.avif`}
              title={t("HomePage.sustentabilidade")}
              href="/sustentabilidade"
              onClick={() => navigate("/sustentabilidade")}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Home;
